import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  from,
  split,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";

import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";

/****************************************** HTTP LINK (unchanged except comments) ************************************************************ */

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_GRAPHQL_URI,
  fetch: async (uri, options) => {
    const opName = options?.headers?.["x-apollo-operation-name"];
    const specialTimeoutOps = new Set([
      "ProductInfo",
      "AllProductsInfo",
      "searchproducts",
      "SortProducts",
      "ProductFilters",
      "ProductsStockOpening",
      "InvoiceDetail",
      "PipelineProducts",
      "RecentlyPurchasedProductsFromSupplier",
      "AccountStock",
      "GetOrderedItems",
      "TempSalesItems",
      "saleOrderItems",
      "GetExcludedItemsOnStatus",
      "PipelineProductsAvailabilityInStock",
      "SearchProductsWithPriceBook",
      "AllDbProducts",
      "CreditNoteDetails",
    ]);

    // Set timeout based on operation name
    const timeoutDuration = specialTimeoutOps.has(opName) ? 20000 : 10000;

    const controller = new AbortController();
    const timeout = setTimeout(() => {
      console.error("Frontend: Operation aborted due to timeout", {
        uri,
        operationName: opName,
        timeoutDuration,
        options,
      });
      controller.abort();
    }, timeoutDuration);

    try {
      if (!options?.headers?.["content-type"]) {
        console.warn("🚨 Missing content-type before fetch!", {
          uri,
          operationName: opName,
          headers: options.headers,
        });
      }

      const response = await fetch(uri, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeout);
      return response;
    } catch (error) {
      if (error.name === "AbortError") {
        console.error("Frontend: Fetch aborted due to timeout", {
          uri,
          operationName: opName,
          timeoutDuration,
        });
      } else {
        console.error("Frontend: Fetch error", {
          uri,
          operationName: opName,
          error,
        });
      }
      throw error;
    }
  },
});

const retryLink = new RetryLink({
  attempts: {
    max: 3,
    retryIf: (error) => {
      return (
        error.networkError && error.networkError.statusCode !== 401 // Don't retry if it's Unauthorized
      );
    },
  },
}).split(
  () => true,
  setContext((operation, prevContext) => {
    const headers = {
      ...prevContext.headers,
      "content-type": "application/json",
    };
    return { headers };
  })
);

export const clientSetup = (item, msalInstance) => {
  // ----------- AUTH LINK (same logic as before, just pulled into a variable) -----------
  const authLink = setContext((operation, prevContext) => {
    const query = operation && operation.query;
    const opName =
      operation?.operationName ||
      query?.definitions?.[0]?.name?.value ||
      "UnknownOperation";

    const existingHeaders = (prevContext && prevContext.headers) || {};
    const headers = {
      ...existingHeaders,
      authorization: item ? `Bearer ${item}` : "",
      "x-apollo-operation-name": String(opName),
      "content-type": "application/json",
      skip: opName === "UpdateToken" ? "true" : "false",
    };

    const serverswitchOps = new Set([
      "ProductInfo",
      "AllProductsInfo",
      "searchproducts",
      "SortProducts",
      "ProductFilters",
      "ProductsStockOpening",
      "InvoiceDetail",
      "PipelineProducts",
      "RecentlyPurchasedProductsFromSupplier",
      "AccountStock",
      "GetOrderedItems",
      "TempSalesItems",
      "saleOrderItems",
      "GetExcludedItemsOnStatus",
      "PipelineProductsAvailabilityInStock",
      "SearchProductsWithPriceBook",
      "AllDbProducts",
      "CreditNoteDetails",
    ]);

    if (serverswitchOps.has(opName)) {
      headers.serverswitch = "true";
    }

    return { headers };
  });

  // ----------- ERROR LINK (same as your Shalini code, just in a variable) -----------
  const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        console.error("GraphQL Error", {
          operationName: operation?.operationName,
          variables: operation?.variables,
          message: err.message,
        });

        if (err.extensions.code === "Unauthorized") {
          msalInstance.logoutRedirect();
          return;
        }
        console.log("graphQlError", err);
        if (err?.extensions?.http?.status === 404) {
          window.dispatchEvent(new CustomEvent("apollo-not-found"));
          return;
        }

        if (err.message?.toLowerCase().includes("insufficient privileges")) {
          return;
        }

        window.dispatchEvent(
          new CustomEvent("apollo-error", {
            detail: err.message,
          })
        );
      }
      return;
    }

    if (networkError) {
      const errorMessage = networkError.message;

      console.error("Network Error", {
        operationName: operation?.operationName,
        variables: operation?.variables,
        message: errorMessage,
      });

      if (errorMessage === "signal is aborted without reason") {
        window.dispatchEvent(
          new CustomEvent("apollo-error", {
            detail:
              "Frontend: We are unable to connect to the server. Please try again later",
          })
        );
        return;
      }

      if (!errorMessage || errorMessage.includes("timeout")) {
        console.error(
          "Frontend: Network timeout or no error message received",
          {
            operationName: operation?.operationName,
            variables: operation?.variables,
          }
        );

        window.dispatchEvent(
          new CustomEvent("apollo-error", {
            detail:
              "Frontend: The request timed out. Please try again later.",
          })
        );
        return;
      }

      window.dispatchEvent(
        new CustomEvent("apollo-error", { detail: errorMessage })
      );
    }
  });

  // ----------- HTTP CHAIN (auth + errors + http) -----------
  const httpChain = from([
    authLink,
    errorLink,
    // you can add retryLink here if you want:
    // retryLink,
    httpLink,
  ]);

  // ----------- WEBSOCKET LINK FOR SUBSCRIPTIONS -----------
  // Derive WS URL:
  // If you have REACT_APP_GRAPHQL_WS_URI, use that. Otherwise, convert HTTP URI.
  const httpUri = process.env.REACT_APP_GRAPHQL_URI || "";
  const explicitWsUri = process.env.REACT_APP_GRAPHQL_WS_URI;

  let wsUri = explicitWsUri;
  if (!wsUri && httpUri) {
    // e.g. http://host[/graphql] -> ws://host/graphql
    const base = httpUri.replace(/^http/, "ws");
    if (base.endsWith("/graphql")) {
      wsUri = base;
    } else {
      wsUri = `${base.replace(/\/+$/, "")}/graphql`;
    }
  }

  const wsLink =
    wsUri &&
    new GraphQLWsLink(
      createClient({
        url: wsUri,
        connectionParams: () => ({
          // send auth token to WS server as well
          authorization: item ? `Bearer ${item}` : "",
        }),
        lazy: true,
        // Automatic reconnection configuration
        shouldRetry: () => true, // Always attempt to reconnect
        retryAttempts: Infinity, // Keep trying indefinitely
        retryWait: (retries) => {
          // Exponential backoff: 1s, 2s, 4s, 8s, max 30s
          return new Promise((resolve) => {
            const delay = Math.min(1000 * Math.pow(2, retries), 30000);
            console.log(`WebSocket reconnecting in ${delay}ms (attempt ${retries + 1})`);
            setTimeout(resolve, delay);
          });
        },
        on: {
          connected: () => console.log('WebSocket connected'),
          closed: () => console.log('WebSocket closed'),
          error: (error) => console.error('WebSocket error:', error),
        },
      })
    );

  // ----------- SPLIT: subscriptions -> WS, others -> HTTP -----------
  const link = wsLink
    ? split(
      ({ query }) => {
        const def = getMainDefinition(query);
        return (
          def.kind === "OperationDefinition" &&
          def.operation === "subscription"
        );
      },
      wsLink,
      httpChain
    )
    : httpChain; // fallback: if wsUri missing, still work over HTTP

  return new ApolloClient({
    link,
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "network-only",
      },
    },
  });
};

// This can stay as a simple HTTP-only client if you still use it somewhere else
const setup = new ApolloClient({
  uri: process.env.REACT_APP_GRAPHQL_URI,
  cache: new InMemoryCache(),
});

export default setup;
